// Filter option lists, level styling and pagination config for the Log Monitor.
// Values (the `value` strings) are sent verbatim to the backend as query params.

export interface SelectOption {
  value: string;
  label: string;
}

export const SERVICE_OPTIONS: SelectOption[] = [
  { value: "all", label: "All" },
  { value: "flight", label: "Flight" },
  { value: "hotel", label: "Hotel" },
  { value: "activity", label: "Activity" },
  { value: "common", label: "Flight+Hotel" },
  { value: "general", label: "General" },
];

export const ENV_OPTIONS: SelectOption[] = [
  { value: "all", label: "All" },
  { value: "production", label: "Production" },
  { value: "pat", label: "PAT" },
  { value: "uat", label: "UAT" },
  { value: "dev", label: "Development" },
];

export const EVENT_OPTIONS: SelectOption[] = [
  { value: "all", label: "All" },
  { value: "error", label: "Error" },
  { value: "warn", label: "Warn" },
  { value: "info", label: "Info" },
];

// Repo / log-source filter. Each option maps a friendly repo label to one or more
// Loggly tags, sent via the backend's `tag` filter. An array is AND-matched by the
// backend (the log must carry ALL the tags). "" / empty tags = no tag filter (all repos).
// FE API frontend vs backend are distinguished by machine tag (machine1 / machine2).
// NOTE: booking-engine / flight-engine tag strings are still best-guess — confirm with
// backend and adjust `tags` here if needed (hotel-engine + fe-api/machineN are confirmed).
export interface RepoOption {
  value: string; // unique dropdown key
  label: string;
  tags: string[]; // Loggly tags to send as `tag` (AND-matched); [] = no filter
}

export const REPO_OPTIONS: RepoOption[] = [
  { value: "", label: "All Repos", tags: [] },
  { value: "booking-engine", label: "Booking Engine", tags: ["booking-engine"] },
  { value: "flight-engine", label: "Flight Engine", tags: ["flight-engine"] },
  { value: "hotel-engine", label: "Hotel Engine", tags: ["hotel-engine"] },
  { value: "fe-api-frontend", label: "FE API Frontend", tags: ["fe-api", "machine1"] },
  { value: "fe-api-backend", label: "FE API Backend", tags: ["fe-api", "machine2"] },
];

// Suggested tags for the multi-select Tags filter (free entry is also allowed).
export const KNOWN_TAGS: string[] = [
  "booking-engine",
  "flight-engine",
  "hotel-engine",
  "fe-api",
  "machine1",
  "machine2",
];

export interface LevelStyle {
  short: string; // compact label for the table chip (ERR/WRN/INF)
  long: string; // full label for the detail drawer chip (Error/Warning/Info)
  bg: string;
  color: string;
}

// Keyed by UPPERCASE level. Both WARN and WARNING map to the warning style.
// Callers must fall back to DEFAULT_LEVEL_STYLE for unknown levels (e.g. UNKNOWN/DEBUG).
export const LEVEL_STYLE: Record<string, LevelStyle> = {
  ERROR: { short: "ERR", long: "Error", bg: "#fce8e6", color: "#c5221f" },
  WARN: { short: "WRN", long: "Warning", bg: "#fef7e0", color: "#e37400" },
  WARNING: { short: "WRN", long: "Warning", bg: "#fef7e0", color: "#e37400" },
  INFO: { short: "INF", long: "Info", bg: "#e8f0fe", color: "#1967d2" },
};

export const DEFAULT_LEVEL_STYLE: LevelStyle = {
  short: "",
  long: "",
  bg: "#f1f3f4",
  color: "#5f6368",
};

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];
